import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Plus, Trash2, Loader2 } from "lucide-react";

const EMPTY_COMP = { nom: "", prenoms: "", date_naissance: "", lieu_naissance: "", nationalite: "", mineur: false };

export default function PolicePage() {
  const { bookingId } = useParams();
  const [form, setForm] = useState({
    booking_id: bookingId || "", nom: "", prenoms: "", date_naissance: "", lieu_naissance: "",
    nationalite: "", domicile: "", email: "", telephone: "", arrivee: "", depart: "", accompagnants: [],
  });
  const [stay, setStay] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/police/booking/${bookingId}`).then((r) => {
      setStay(r.data);
      setForm((f) => ({ ...f, arrivee: r.data.arrivee || "", depart: r.data.depart || "" }));
    }).catch(() => {});
  }, [bookingId]);

  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addComp = () => setForm({ ...form, accompagnants: [...form.accompagnants, { ...EMPTY_COMP }] });
  const rmComp = (i) => setForm({ ...form, accompagnants: form.accompagnants.filter((_, x) => x !== i) });
  const setComp = (i, k, v) => setForm({ ...form, accompagnants: form.accompagnants.map((c, x) => x === i ? { ...c, [k]: v } : c) });

  const submit = async () => {
    if (!form.nom || !form.prenoms || !form.date_naissance || !form.nationalite) {
      return toast.error("Merci de renseigner nom, prénom(s), date de naissance et nationalité.");
    }
    setSubmitting(true);
    try {
      await api.post("/police/fiche", form);
      setDone(true);
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const input = "w-full rounded-lg border border-ink-400/25 px-3 py-2.5 text-sm outline-none focus:border-brand bg-white";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-20" data-testid="police-done">
        <div className="max-w-md w-full rounded-3xl bg-white border border-black/5 shadow-[0_16px_48px_rgba(44,48,48,0.1)] p-8 text-center">
          <CheckCircle2 size={56} className="mx-auto text-brand mb-4" strokeWidth={1.5} />
          <h1 className="font-heading text-3xl text-ink-900 mb-2">Fiche enregistrée</h1>
          <p className="text-ink-600">Merci, votre fiche de police a bien été transmise au Domaine de La Bégaudière.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-cream py-16 px-4" data-testid="police-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-full bg-brand/10 flex items-center justify-center text-brand"><ShieldCheck size={22} /></span>
          <h1 className="font-heading text-3xl text-ink-900">Fiche individuelle de police</h1>
        </div>
        <p className="text-sm text-ink-500 mb-1">Fiche d'hébergement · art. R.611-42 du CESEDA</p>
        <p className="text-sm text-ink-500 mb-6">Obligatoire pour tout voyageur de nationalité étrangère. <span className="text-ink-400">Required for travellers of foreign nationality.</span></p>

        {stay && (
          <div className="rounded-xl bg-white border border-black/5 p-4 text-sm mb-6 flex flex-wrap gap-x-8 gap-y-1">
            <span className="text-ink-400">Hébergement : <b className="text-ink-900">{stay.accommodation_name || "—"}</b></span>
            <span className="text-ink-400">Séjour : <b className="text-ink-900">{stay.arrivee} → {stay.depart}</b></span>
          </div>
        )}

        <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-6 space-y-4">
          <h2 className="text-sm font-semibold text-brand">Voyageur principal</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>Nom *</label><input className={input} value={form.nom} onChange={s("nom")} data-testid="police-nom" /></div>
            <div><label className={label}>Prénom(s) *</label><input className={input} value={form.prenoms} onChange={s("prenoms")} data-testid="police-prenoms" /></div>
            <div><label className={label}>Date de naissance *</label><input type="date" className={input} value={form.date_naissance} onChange={s("date_naissance")} data-testid="police-dob" /></div>
            <div><label className={label}>Lieu de naissance</label><input className={input} value={form.lieu_naissance} onChange={s("lieu_naissance")} placeholder="Ville, pays" /></div>
            <div><label className={label}>Nationalité *</label><input className={input} value={form.nationalite} onChange={s("nationalite")} data-testid="police-nationalite" /></div>
            <div><label className={label}>Domicile habituel</label><input className={input} value={form.domicile} onChange={s("domicile")} placeholder="Adresse complète" /></div>
            <div><label className={label}>E-mail</label><input className={input} value={form.email} onChange={s("email")} /></div>
            <div><label className={label}>Téléphone</label><input className={input} value={form.telephone} onChange={s("telephone")} /></div>
          </div>
          {!stay && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={label}>Date d'arrivée</label><input type="date" className={input} value={form.arrivee} onChange={s("arrivee")} /></div>
              <div><label className={label}>Date de départ</label><input type="date" className={input} value={form.depart} onChange={s("depart")} /></div>
            </div>
          )}

          <div className="pt-2 border-t border-black/5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-brand">Voyageurs accompagnants (dont mineurs)</h2>
              <button type="button" onClick={addComp} className="inline-flex items-center gap-1.5 text-sm text-brand font-medium hover:underline" data-testid="police-add-comp"><Plus size={15} /> Ajouter</button>
            </div>
            {form.accompagnants.length === 0 && <p className="text-xs text-ink-400">Aucun accompagnant. Ajoutez chaque voyageur étranger supplémentaire.</p>}
            <div className="space-y-4">
              {form.accompagnants.map((c, i) => (
                <div key={i} className="rounded-xl bg-cream/60 border border-black/5 p-4" data-testid={`police-comp-${i}`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-medium text-ink-600">Accompagnant {i + 1}</span>
                    <button type="button" onClick={() => rmComp(i)} className="text-ink-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className={input} placeholder="Nom" value={c.nom} onChange={(e) => setComp(i, "nom", e.target.value)} />
                    <input className={input} placeholder="Prénom(s)" value={c.prenoms} onChange={(e) => setComp(i, "prenoms", e.target.value)} />
                    <input type="date" className={input} value={c.date_naissance} onChange={(e) => setComp(i, "date_naissance", e.target.value)} />
                    <input className={input} placeholder="Lieu de naissance" value={c.lieu_naissance} onChange={(e) => setComp(i, "lieu_naissance", e.target.value)} />
                    <input className={input} placeholder="Nationalité" value={c.nationalite} onChange={(e) => setComp(i, "nationalite", e.target.value)} />
                    <label className="flex items-center gap-2 text-sm text-ink-600"><input type="checkbox" checked={c.mineur} onChange={(e) => setComp(i, "mineur", e.target.checked)} /> Mineur</label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-ink-400 pt-2">Ces données sont conservées par le loueur et tenues à la disposition des services de police et de gendarmerie pendant 6 mois, puis supprimées (RGPD).</p>
          <button onClick={submit} disabled={submitting} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="police-submit">
            {submitting ? <><Loader2 size={18} className="animate-spin" /> Envoi…</> : "Envoyer ma fiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
